import { useState } from "react";
import { AlertTriangle, Trash2, Copy } from "lucide-react";
import { Notify, toast } from "@/components/ui/notify";
import { Button } from "@/components/ui/button";
import type { MachineDetail } from "@/features/lines/hooks";
import { useDeleteMachine, useDuplicateMachine } from "@/features/line-editor/hooks";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { formatRelativeTime } from "@/lib/display-labels";
import { useAuditHistory, type AuditEntry } from "./hooks";
import type { MachineChangeEvent, SidebarMode } from "./MachineSidebar";

interface Props {
  machine: MachineDetail;
  lineId: string;
  mode: SidebarMode;
  onMachineChange?: (event: MachineChangeEvent) => void;
}

export function AdvancedActionsTab({ machine, lineId, mode, onMachineChange }: Props) {
  const isView = mode === "view";
  const remove = useDeleteMachine(lineId);
  const duplicate = useDuplicateMachine(lineId);
  const auditQuery = useAuditHistory({
    entityType: "Machine",
    entityId: machine.id,
    limit: 50,
  });
  const { data: audit = [], isLoading: loadingAudit } = auditQuery;

  const [confirming, setConfirming] = useState(false);

  async function handleRemove() {
    try {
      await remove.mutateAsync(machine.id);
      toast.success(`Máquina ${machine.name} removida.`);
      onMachineChange?.({ type: "removed", machineId: machine.id });
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao remover.", { description: msg });
      setConfirming(false);
    }
  }

  async function handleDuplicate() {
    try {
      const clone = await duplicate.mutateAsync({ machineId: machine.id });
      toast.success(`Máquina ${machine.name} duplicada.`, {
        description: `Criada "${clone.code}". Posicione a cópia no canvas.`,
      });
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao duplicar.", { description: msg });
    }
  }

  return (
    <div className="space-y-4">
      <section>
        <h3 className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
          Remover máquina
        </h3>
        {isView ? (
          <p className="text-xs text-muted-foreground">Apenas gestores podem remover máquinas.</p>
        ) : !confirming ? (
          <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
            <p>
              Remove o nó da máquina do canvas e suas conexões. Os demais nós permanecem nas mesmas
              coordenadas.
            </p>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setConfirming(true)}
              disabled={remove.isPending}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Remover {machine.name}
            </Button>
          </div>
        ) : (
          <div className="space-y-2 rounded-md border border-destructive bg-destructive/10 p-3 text-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <p>
                Confirmar remoção de <strong>{machine.name}</strong>? As conexões associadas serão
                removidas. INÍCIO e FIM permanecem. Esta ação é irreversível.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="destructive"
                size="sm"
                className="flex-1"
                onClick={() => void handleRemove()}
                disabled={remove.isPending}
              >
                {remove.isPending ? "Removendo…" : "Confirmar remoção"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirming(false)}
                disabled={remove.isPending}
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}
      </section>

      <section>
        <h3 className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
          Duplicar máquina
        </h3>
        {isView ? (
          <p className="text-xs text-muted-foreground">Apenas gestores podem duplicar máquinas.</p>
        ) : (
          <div className="space-y-2 rounded-md border bg-muted/30 p-3 text-sm">
            <p>
              Cria uma cópia com os mesmos parâmetros e compatibilidade. A cópia não herda histórico
              nem sensores e precisa ser posicionada no canvas.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleDuplicate()}
              disabled={duplicate.isPending}
            >
              <Copy className="h-3.5 w-3.5" />
              {duplicate.isPending ? "Duplicando…" : "Duplicar configuração"}
            </Button>
          </div>
        )}
      </section>

      <section>
        <h3 className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
          Histórico de alterações
        </h3>
        {loadingAudit ? (
          <p className="text-xs text-muted-foreground">Carregando histórico…</p>
        ) : auditQuery.isError ? (
          <Notify.QueryError
            variant="inline"
            error={auditQuery.error}
            onRetry={() => void auditQuery.refetch()}
            title="Não foi possível carregar o histórico"
          />
        ) : audit.length === 0 ? (
          <p className="text-xs text-muted-foreground">Sem alterações registradas.</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {audit.map((a: AuditEntry) => (
              <li key={a.id} className="space-y-0.5 rounded border px-2 py-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono uppercase text-muted-foreground">{a.action}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {formatRelativeTime(a.createdAt)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
